// Needs you (desktop): what is blocked until you act, each move with its actions inline. The moves, and what
// happens when you act on them, come from useMoves (the phone shows the same moves as rows that open a sheet).
import { useRef } from 'react';
import { ToLink } from '../../ui/ActLink';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import type { ProposedAction, SessionUser, TeamCheck, TodayQuiet, TodayYours } from '@dpw/shared';
import { spring } from '../../lib/motion';
import { Orb } from '../../ui/motion';
import { useMoreBelow } from './util';
import { useMoves, type Act, type Move } from './moves';

const BT = { pri: 'bt pri', sec: 'bt', ghost: 'bt ghost' } as const;
// A link from Today: an activity opens in its panel beside Today (6.2); anything else is its page.
const To = ToLink;
function ActButton({ a }: { a: Act }) {
  return a.to ? <To className={BT[a.style]} to={a.to}>{a.label}</To>
    : <button type="button" className={BT[a.style]} disabled={a.disabled} onClick={a.run}>{a.label}</button>;
}

export function NeedsYou({ me, actions, checks, yours, quiet, onCount }: {
  me: SessionUser; actions: ProposedAction[]; checks: TeamCheck[]; yours: TodayYours[]; quiet: TodayQuiet[];
  onCount: (open: number, cleared: number) => void;
}) {
  const mv = useMoves({ me, actions, checks, yours, quiet, onCount });
  const scroll = useRef<HTMLDivElement>(null);
  useMoreBelow(scroll);
  const { done, open, cleared } = mv;
  const total = open + cleared;

  const row = (m: Move) => {
    const d = done[m.key];
    return (
      <motion.div key={m.key} layout="position" transition={spring.gentle} data-row data-card={m.key} data-kind={m.kind} className={`move${m.ai ? ' ai' : ''}${d ? ' done' : ''}`}>
        <span className={`mi ${d ? 'grn' : m.tone}`}>{m.icon}</span>
        <div className="mt">
          <div className="ml1"><span className={m.lbl[1]}>{m.lbl[0]}</span><b>{m.title}</b></div>
          {!d && <div className="why2">{m.why}</div>}
          <div className="ml3">{m.ctx && <To className="ctx" to={m.ctx.to}><i />{m.ctx.label}</To>}
            <AnimatePresence mode="wait" initial={false}>
              {d ? (
                <motion.div key="d" className="acts dn" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <span className="xs mut" role="status">{d.result}</span>{d.undo && <button type="button" className="bt" onClick={d.undo}>Undo</button>}
                </motion.div>
              ) : (
                <motion.div key="o" className="acts" exit={{ opacity: 0 }}>
                  {m.answer ? (
                    <form className="ansrow" onSubmit={e => { e.preventDefault(); mv.sendAnswer(m); }}>
                      <input className="inp" value={mv.answerOf(m)} onChange={e => mv.setAnswer(m, e.target.value)} placeholder="Your answer" aria-label={`Your answer: ${m.answer.question}`} />
                      <button className="bt" disabled={!mv.answerOf(m).trim()}>Answer</button>
                    </form>
                  ) : m.acts.map(a => <ActButton key={a.label} a={a} />)}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <section className="nyc" aria-label="Needs you">
      <div className="sech"><div><b>Needs you</b><span>Blocked until you act</span></div>
        <div className="meter"><span>{cleared} of {total} cleared</span><span className="bar"><motion.i animate={{ scaleX: total ? cleared / total : 0 }} transition={spring.gentle} /></span></div></div>
      <div className="pscr" ref={scroll}>
        <LayoutGroup id="needs">
          <div className="grh ai"><Orb size={8} />From the assistant<span className="n">{mv.aiShown.filter(m => !done[m.key]).length}</span></div>
          {mv.aiShown.length ? mv.aiShown.map(row) : <div className="emp2">Nothing from the assistant.</div>}
          <div className="grh">Yours<span className="n">{mv.mineShown.filter(m => !done[m.key]).length}</span></div>
          {mv.mineShown.length ? mv.mineShown.map(row) : <div className="emp2">Nothing of yours is at risk.</div>}
          <div className="grh">Gone quiet<span className="n">{mv.quietRows.filter(r => !r.done).length}</span></div>
          {mv.quietRows.length ? mv.quietRows.map(r => (
            <motion.div key={r.key} layout="position" transition={spring.gentle} data-row className={`gqr${r.done ? ' done' : ''}`}>
              <span className={`dt ${r.q.tone}`} />
              <div className="gt"><To to={r.to}>{r.q.title}</To><span className="why2">{r.q.why}</span></div>
              {r.done ? <span className="ga"><span className="xs gres" role="status">{r.done.result}</span>{r.done.undo && <button type="button" className="bt" onClick={r.done.undo}>Undo</button>}</span> : (
                <span className="ga">
                  {r.nudge && <button type="button" className="bt" onClick={r.nudge}>Nudge {r.who}</button>}
                  <button type="button" className="bt" onClick={r.fine}>It is fine</button>
                </span>
              )}
            </motion.div>
          )) : <div className="emp2">Nothing has gone quiet.</div>}
        </LayoutGroup>
      </div>
    </section>
  );
}
